import { openTelegramLink, shareMessage } from '@telegram-apps/sdk-react';

import { safeTma } from '@common/utils/safeTma';

import { analytics, type MatchmakerShareVia } from '@services/AnalyticsService';
import { type PostMatchmakerRecommendationResponse } from '@services/MatchmakerService';

export type RecommendationShare = Pick<
  PostMatchmakerRecommendationResponse,
  'link' | 'share_text' | 'prepared_message_id'
>;

export type ShareRecommendationOptions = {
  isPreparedAllowed: boolean;
};

export const shareUrlOf = ({ link, share_text }: RecommendationShare) =>
  `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(share_text)}`;

const shareViaOf = (
  share: RecommendationShare,
  { isPreparedAllowed }: ShareRecommendationOptions,
): MatchmakerShareVia => {
  if (
    isPreparedAllowed &&
    share.prepared_message_id &&
    shareMessage.isAvailable()
  ) {
    return 'prepared';
  }

  return openTelegramLink.isAvailable() ? 'share_url' : 'none';
};

/**
 * Sends the invite through Telegram (plan §4, review S7): the bot's prepared
 * message when there is one, else Telegram's share link. The goal goes out
 * before the call, which may put the app in the background.
 *
 * Resolves with how it was sent, and whether the prepared message was refused
 * (cancelled or failed): a next try then uses the link.
 */
export const shareRecommendation = async (
  share: RecommendationShare,
  options: ShareRecommendationOptions,
): Promise<{ via: MatchmakerShareVia; isPreparedRefused: boolean }> => {
  const via = shareViaOf(share, options);

  analytics.trackMmShare(via);

  if (via === 'prepared' && share.prepared_message_id) {
    try {
      await shareMessage(share.prepared_message_id);
    } catch {
      return { via, isPreparedRefused: true };
    }

    return { via, isPreparedRefused: false };
  }

  if (via === 'share_url') {
    safeTma('openTelegramLink', () => openTelegramLink(shareUrlOf(share)));
  }

  return { via, isPreparedRefused: false };
};
